import React from 'react';
import { Button } from 'antd';
import Tooltip from './Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/store';
import { iconButtonStyle, navbarButtonStyle } from '../../utils/styles';
import { NavbarListButton } from '../Layout/NavbarListButton';
import DuotoneLightIcon from './DuotoneLightIcon';
import LabeledControlRow from './LabeledControlRow';
import { ItemTypes } from '../../constants/constants';
import { isASelectionBox } from '../../utils/boxes';
import {
  COLUMN_VERTICAL_ALIGNS,
  resolveVerticalAlignTarget,
} from '../../utils/columns';

// Intitulé de groupe dans la liste déroulante : dit sur quoi porte la série de
// boutons qui suit (la cellule / les lignes, ou l'élément lui-même).
const listGroupTitleStyle = {
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: 'rgba(11, 34, 57, 0.45)',
  padding: '2px 2px 4px',
  textAlign: 'center',
  width: '100%',
  whiteSpace: 'nowrap',
};

// Deuxième groupe : filet de séparation, sinon ses boutons semblent appartenir
// à la série du dessus.
const listSecondGroupTitleStyle = {
  ...listGroupTitleStyle,
  marginTop: 4,
  paddingTop: 6,
  borderTop: '1px solid rgba(11, 34, 57, 0.12)',
};

const selector = (state) => {
  const boxes = [
    ...state.boxes,
    ...state.masks.map((m) => m.boxes).flat(),
  ]
  const box = boxes.find((b) => b.id === state.selectedBoxId)
  return {
    alignBoxDefault: state.alignBoxDefault,
    alignBoxesDrawMode: state.alignBoxesDrawMode,
    alignBoxAbsolutDrawMode: state.alignBoxAbsolutDrawMode,
    groupSelectionBoxes: state.groupSelection.length === 0 ? [] : boxes.filter((b) => state.groupSelection.includes(b.id)),
    box,
    drawMode: box && (!box.columnId || state.columns.find((col) => col.id === box.columnId).drawMode),
    isSelectionBox: isASelectionBox(box?.type),
    distributeBoxesDrawMode: state.distributeBoxesDrawMode,
    // Cellule (ou colonne à lignes) dont la répartition verticale s'applique à
    // cette box, hors mode dessin : dès qu'il y a plusieurs box dans la cellule
    // ou plusieurs lignes dans la colonne, c'est ce niveau qui place le contenu
    // et les réglages par box n'ont plus d'effet (cf. ColumnWrapper.boxPosition).
    verticalAlignTarget: resolveVerticalAlignTarget({
      box,
      columns: state.columns,
      boxes: state.boxes,
      rowContainers: state.rowContainers,
    }),
    setColumnVerticalAlign: state.setColumnVerticalAlign,
  }
};

const BoxAlignmentButton = ({ showLabel = false }) => {
  const { distributeBoxesDrawMode, alignBoxesDrawMode, alignBoxAbsolutDrawMode, groupSelectionBoxes, box, drawMode, alignBoxDefault, isSelectionBox, verticalAlignTarget, setColumnVerticalAlign } = useStore(selector);
  const { t } = useTranslation();
  const iconFontSize = 22
  // Un espace occupe toute sa colonne, comme un saut de ligne : rien à aligner.
  const isHidden =
    !box ||
    box.type === ItemTypes.LINE_BREAK ||
    box.type === ItemTypes.SPACE ||
    isSelectionBox;
  const columnAlignButtons = {
    alignLeft: {
      icon: < span className="fa-layers" style={{ width: '21px' }}>
        <FontAwesomeIcon icon={icon({ name: 'square-dashed', style: 'light' })} style={{ fontSize: iconFontSize }} />
        <FontAwesomeIcon icon={icon({ name: 'arrow-left', transform: 'shrink-6', style: 'light' })} style={{ position: 'absolute', right: '5px' }} />
      </span >,
      positions: { horizontal: "start" },
    },
    alignRight: {
      icon: < span className="fa-layers" style={{ width: '21px' }}>
        <FontAwesomeIcon icon={icon({ name: 'square-dashed', style: 'light' })} style={{ fontSize: iconFontSize }} />
        <FontAwesomeIcon icon={icon({ name: 'arrow-right', transform: 'shrink-6', style: 'light' })} style={{ position: 'absolute', left: '5px' }} />
      </span >,
      positions: { horizontal: "end" },
    },
    alignTop: {
      icon: < span className="fa-layers" style={{ width: '21px' }}>
        <FontAwesomeIcon icon={icon({ name: 'square-dashed', style: 'light' })} style={{ fontSize: iconFontSize }} />
        <FontAwesomeIcon icon={icon({ name: 'arrow-up', transform: 'shrink-6', style: 'light' })} style={{ position: 'absolute', top: '-4px' }} />
      </span >,
      positions: { vertical: "start" },
    },
    alignBottom: {
      icon: < span className="fa-layers" style={{ width: '21px' }}>
        <FontAwesomeIcon icon={icon({ name: 'square-dashed', style: 'light' })} style={{ fontSize: iconFontSize }} />
        <FontAwesomeIcon icon={icon({ name: 'arrow-down', transform: 'shrink-6', style: 'light' })} style={{ position: 'absolute', bottom: '-4px' }} />
      </span >,
      positions: { vertical: "end" },
    },
    alignCenterHorizontal: {
      icon: < span className="fa-layers" style={{ width: '21px' }}>
        <FontAwesomeIcon icon={icon({ name: 'square-dashed', style: 'light' })} style={{ fontSize: iconFontSize }} />
        <FontAwesomeIcon icon={icon({ name: 'border-center-v', family: 'duotone', style: 'solid' })} style={{ "--fa-primary-opacity": "0.5", "--fa-secondary-opacity": "0", fontSize: iconFontSize - 4 }} />
      </span >,
      positions: { horizontal: "center" },
    },
    alignCenterVertical: {
      icon: < span className="fa-layers" style={{ display: 'flex', width: '21px' }}>
        <FontAwesomeIcon icon={icon({ name: 'square-dashed', style: 'light' })} style={{ fontSize: iconFontSize }} />
        <FontAwesomeIcon icon={icon({ name: 'border-center-h', family: 'duotone', style: 'solid' })} style={{ "--fa-primary-opacity": "0.5", "--fa-secondary-opacity": "0", fontSize: iconFontSize - 4 }} />
      </span >,
      positions: { vertical: "center" },
    },
  };

  const relativeAlignButtons = {
    alignLeft: {
      icon: < FontAwesomeIcon icon={icon({ name: 'objects-align-left', style: 'light' })} style={iconButtonStyle()} />,
      side: 'left',
      type: 'align'
    },
    alignRight: {
      icon: < FontAwesomeIcon icon={icon({ name: 'objects-align-right', style: 'light' })} style={iconButtonStyle()} />,
      side: 'right',
      type: 'align'
    },
    alignTop: {
      icon: < FontAwesomeIcon icon={icon({ name: 'objects-align-top', style: 'light' })} style={iconButtonStyle()} />,
      side: 'top',
      type: 'align'
    },
    alignBottom: {
      icon: < FontAwesomeIcon icon={icon({ name: 'objects-align-bottom', style: 'light' })} style={iconButtonStyle()} />,
      side: 'bottom',
      type: 'align'
    },
    alignVertical: {
      icon: < FontAwesomeIcon icon={icon({ name: 'objects-align-center-vertical', style: 'light' })} style={iconButtonStyle()} />,
      side: 'vertical',
      type: 'align'
    },
    alignHorizontal: {
      icon: < FontAwesomeIcon icon={icon({ name: 'objects-align-center-horizontal', style: 'light' })} style={iconButtonStyle()} />,
      side: 'horizontal',
      type: 'align'
    },
    distributeVertical: {
      icon: < FontAwesomeIcon icon={icon({ name: 'distribute-spacing-vertical', style: 'light' })} style={iconButtonStyle()} />,
      side: 'vertical',
      type: 'distribute'
    },
    distributeHorizontal: {
      icon: < FontAwesomeIcon icon={icon({ name: 'distribute-spacing-horizontal', style: 'light' })} style={iconButtonStyle()} />,
      side: 'horizontal',
      type: 'distribute'
    },
  };

  const absoluteAlignButtons = {
    alignToMarginLeft: {
      icon: < FontAwesomeIcon icon={icon({ name: 'arrow-left-to-line', style: 'light' })} style={iconButtonStyle()} />,
      side: 'left',
    },
    alignToMarginRight: {
      icon: < FontAwesomeIcon icon={icon({ name: 'arrow-right-to-line', style: 'light' })} style={iconButtonStyle()} />,
      side: 'right',
    },
    alignToMarginTop: {
      icon: < FontAwesomeIcon icon={icon({ name: 'arrow-up-to-line', style: 'light' })} style={iconButtonStyle()} />,
      side: 'top',
    },
    alignToMarginBottom: {
      icon: < FontAwesomeIcon icon={icon({ name: 'arrow-down-to-line', style: 'light' })} style={iconButtonStyle()} />,
      side: 'bottom',
    },
    alignToMarginHorizontalCenter: {
      icon: <FontAwesomeIcon icon={icon({ name: 'border-center-h', style: 'light' })} style={{ ...iconButtonStyle(), transform: 'rotate(90deg)' }} />,
      side: 'horizontal-center',
    },
    alignToMarginVerticalCenter: {
      icon: <FontAwesomeIcon icon={icon({ name: 'border-center-h', style: 'light' })} style={iconButtonStyle()} />,
      side: 'vertical-center',
    },
  };

  // Réglages de la cellule, hors mode dessin : ils placent la pile de box dans
  // la hauteur de la cellule. « Réparti » pose la première box en haut, la
  // dernière en bas et des espaces égaux entre les autres.
  const cellAlignButtons = {
    alignTop: {
      icon: <FontAwesomeIcon icon={icon({ name: 'objects-align-top', style: 'light' })} />,
      value: COLUMN_VERTICAL_ALIGNS.TOP,
    },
    alignVertical: {
      icon: <FontAwesomeIcon icon={icon({ name: 'objects-align-center-vertical', style: 'light' })} />,
      value: COLUMN_VERTICAL_ALIGNS.CENTER,
    },
    alignBottom: {
      icon: <FontAwesomeIcon icon={icon({ name: 'objects-align-bottom', style: 'light' })} />,
      value: COLUMN_VERTICAL_ALIGNS.BOTTOM,
    },
    distributeVertical: {
      icon: <FontAwesomeIcon icon={icon({ name: 'distribute-spacing-vertical', style: 'light' })} />,
      value: COLUMN_VERTICAL_ALIGNS.BETWEEN,
    },
  };

  // Panel de droite (showLabel) : masquée = pas de ligne (évite un libellé orphelin).
  if (showLabel && isHidden) return null;

  // Un groupe donne accès aux deux familles : aligner/répartir ses box entre
  // elles (comme la multi-sélection dont il est issu) et aligner le groupe
  // entier sur les marges de la page.
  const isGroupBox = box?.type === ItemTypes.GROUP_BOXES;
  const showRelativeAlign =
    drawMode && (isGroupBox || groupSelectionBoxes.length !== 0);
  const showAbsoluteAlign =
    drawMode && (isGroupBox || groupSelectionBoxes.length === 0);
  // Cellule à plusieurs box, ou colonne découpée en plusieurs lignes : elle
  // gagne ses propres réglages (dont la répartition).
  const showCellAlign = !drawMode && !!verticalAlignTarget;
  const cellVerticalAlign =
    verticalAlignTarget?.column?.verticalAlign ?? COLUMN_VERTICAL_ALIGNS.CENTER;
  // Top/centre/bas/réparti ne bougent rien si la cellule n'a pas de hauteur
  // libre au-delà de son contenu (cf. columnStackStyle) : un bouton actif
  // sans effet visible se lit comme un bug. cellEl introuvable (pas encore
  // monté) : ne pas griser à tort.
  const hasCellFreeHeight = showCellAlign
    ? (() => {
        const cellEl = document.getElementById(`column-${verticalAlignTarget.column.id}`);
        return !cellEl || cellEl.scrollHeight > cellEl.clientHeight + 1;
      })()
    : false;
  // Les réglages par box ne disparaissent que là où ils n'ont aucun effet :
  // dans une cellule qui porte plusieurs box, c'est la cellule qui place la
  // pile entière (cf. ColumnWrapper.boxPosition). Une box seule dans sa cellule
  // — le cas d'une colonne découpée en lignes — garde le sien, gauche/droite
  // compris.
  const showBoxAlign = !drawMode && verticalAlignTarget?.kind !== 'boxes';

  const alignmentList = (
    <NavbarListButton
      key="AlignmentList"
      title={t('alignment')}
      disabled={isHidden}
      hidden={isHidden}
      icon={<DuotoneLightIcon name='objects-align-left' style={iconButtonStyle()} />}
    >
      {showRelativeAlign &&
        Object.keys(relativeAlignButtons).map((buttonName) => {
          return (
            <Tooltip title={t(buttonName)} placement='right' mouseLeaveDelay='0'>
              <Button
                key={`alignment${buttonName}`}
                style={{
                  ...navbarButtonStyle,
                  marginBottom: '1px',
                }}
                disabled={false}
                icon={relativeAlignButtons[buttonName].icon}
                onMouseDown={() => relativeAlignButtons[buttonName].type === 'align' ?
                  alignBoxesDrawMode(relativeAlignButtons[buttonName].side) :
                  distributeBoxesDrawMode(relativeAlignButtons[buttonName].side)}
              />
            </Tooltip>
          );
        })}
      {/* Séparateur entre « entre elles » et « sur la page » : les deux
          familles cohabitent seulement pour un groupe. */}
      {showRelativeAlign && showAbsoluteAlign && (
        <div style={{ height: 1, width: '100%', backgroundColor: 'rgba(11, 34, 57, 0.15)', margin: '4px 0' }} />
      )}
      {showAbsoluteAlign &&
        Object.keys(absoluteAlignButtons).map((buttonName) => {
          return (
            <Tooltip title={t(buttonName)} placement='right' mouseLeaveDelay='0'>
              <Button
                key={`alignment${buttonName}`}
                style={{
                  ...navbarButtonStyle,
                  marginBottom: '1px',
                }}
                disabled={false}
                icon={absoluteAlignButtons[buttonName].icon}
                onMouseDown={() => alignBoxAbsolutDrawMode(absoluteAlignButtons[buttonName].side)}
              />
            </Tooltip>
          );
        })}
      {/* Titre : dire que le clic déplace toute la pile de la cellule (ou toutes
          les lignes de la colonne), pas seulement la box sélectionnée. */}
      {showCellAlign && (
        <div style={listGroupTitleStyle}>
          {t(verticalAlignTarget?.kind === 'rows' ? 'rows' : 'cell')}
        </div>
      )}
      {showCellAlign &&
        Object.keys(cellAlignButtons).map((buttonName) => {
          const { icon: buttonIcon, value } = cellAlignButtons[buttonName];
          return (
            <Tooltip
              key={buttonName}
              title={hasCellFreeHeight ? t(buttonName) : t('cell-align-no-effect-hint')}
              placement='right'
              mouseLeaveDelay='0'
            >
              <Button
                key={`cellAlignment${buttonName}`}
                style={{
                  ...navbarButtonStyle,
                  marginBottom: '1px',
                }}
                disabled={!hasCellFreeHeight}
                icon={React.cloneElement(buttonIcon, {
                  style: iconButtonStyle(cellVerticalAlign === value),
                })}
                onMouseDown={() =>
                  setColumnVerticalAlign(verticalAlignTarget.column.id, value)
                }
              />
            </Tooltip>
          );
        })}
      {/* Second groupe : ce que la box fait dans sa propre cellule (gauche,
          droite, centres). Il cohabite avec le premier quand une colonne est
          découpée en lignes — la box est alors seule dans sa cellule. */}
      {showCellAlign && showBoxAlign && (
        <div style={listSecondGroupTitleStyle}>{t('element')}</div>
      )}
      {showBoxAlign &&
        Object.keys(columnAlignButtons).map((buttonName) => {
          return (
            <Tooltip title={t(buttonName)} placement='right' mouseLeaveDelay='0'>
              <Button
                key={`alignment${buttonName}`}
                style={{
                  ...navbarButtonStyle,
                  marginBottom: '1px',
                }}
                disabled={false}
                icon={columnAlignButtons[buttonName].icon}
                onMouseDown={() => alignBoxDefault(columnAlignButtons[buttonName].positions)}
              />
            </Tooltip>
          );
        })}
    </NavbarListButton>
  );

  if (showLabel) {
    // Libellé verbe court, comme les autres tuiles (Grouper/Copier/Supprimer) :
    // « Alignement » débordait de sa tuile dès que la section en compte quatre.
    return <LabeledControlRow control={alignmentList} label={t('align')} />;
  }
  return alignmentList;
};

export default BoxAlignmentButton;
